import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { IconFolderFilled, IconX } from '@tabler/icons-react'
import { useTranslate } from 'react-admin'
import { useNavigate } from 'react-router-dom'
import { FileCopyHelper } from '../../helpers/FileCopy.helper'
import { NumberFormatter } from '../../helpers/NumberFormatter.helper'
import { CreateFileCopyPayload, FileCopyService } from '../../services/FileCopy.service'
import {
  DriveFolderChild,
  DriveFolderChildren,
  FileCopyDryRunResult,
  FileCopySelection,
} from '../../types/FileCopy'

type BrowseEntry = DriveFolderChild & { isFolder: boolean }

type SelectedEntry = {
  id: string
  name: string
  /** Drive path of the folder the entry was checked in */
  location: string
  sizeBytes?: number
}

type SelectionState = {
  folders: Map<string, SelectedEntry>
  files: Map<string, SelectedEntry>
}

const toggleSelected = (
  entries: Map<string, SelectedEntry>,
  entry: SelectedEntry,
): Map<string, SelectedEntry> => {
  const next = new Map(entries)
  if (next.has(entry.id)) {
    next.delete(entry.id)
  } else {
    next.set(entry.id, entry)
  }
  return next
}

type Props = {
  onCreated?: (operationId: string) => void
}

export const CreateFileCopy = ({ onCreated }: Props): JSX.Element => {
  const t = useTranslate()
  const navigate = useNavigate()

  const [browseFolderId, setBrowseFolderId] = useState<string | null>(null)
  const [path, setPath] = useState<Array<{ id: string; name: string }>>([])
  const [selection, setSelection] = useState<SelectionState>({
    folders: new Map(),
    files: new Map(),
  })
  const [destination, setDestination] = useState({ subFolderPrefix: '' })
  const [keepTree, setKeepTree] = useState(true)
  const [overwriteExisting, setOverwriteExisting] = useState(false)

  const rootsQuery = useQuery({
    queryKey: ['fileCopy', 'driveRoots'],
    queryFn: FileCopyService.listDriveRoots,
  })

  const childrenQuery = useQuery({
    queryKey: ['fileCopy', 'folderChildren', browseFolderId],
    queryFn: () => FileCopyService.listFolderChildren(browseFolderId as string),
    enabled: !!browseFolderId,
  })

  const entries = useMemo<BrowseEntry[]>(() => {
    if (!browseFolderId) {
      return (rootsQuery.data ?? []).map(root => ({ ...root, isFolder: true, isSupported: true }))
    }
    const { folders = [], files = [] } = (childrenQuery.data ?? {}) as DriveFolderChildren
    return [
      ...folders.map(f => ({ ...f, isFolder: true, isSupported: true })),
      ...files.map(f => ({ ...f, isFolder: false })),
    ]
  }, [browseFolderId, rootsQuery.data, childrenQuery.data])

  const toggleEntry = useCallback(
    (entry: BrowseEntry) => {
      const selected: SelectedEntry = {
        id: entry.id,
        name: entry.name,
        location: ['Drive', ...path.map(p => p.name)].join(' / '),
        sizeBytes: entry.sizeBytes,
      }
      setSelection(prev =>
        entry.isFolder
          ? { ...prev, folders: toggleSelected(prev.folders, selected) }
          : { ...prev, files: toggleSelected(prev.files, selected) },
      )
    },
    [path],
  )

  const unselect = useCallback((kind: keyof SelectionState, id: string) => {
    setSelection(prev => {
      const next = new Map(prev[kind])
      next.delete(id)
      return { ...prev, [kind]: next }
    })
  }, [])

  const clearSelection = useCallback(() => {
    setSelection({ folders: new Map(), files: new Map() })
  }, [])

  const buildPayload = useCallback((): CreateFileCopyPayload => {
    const selectionPayload: FileCopySelection = {
      folderIds: Array.from(selection.folders.keys()),
      fileIds: Array.from(selection.files.keys()),
    }
    return {
      selection: selectionPayload,
      destination: {
        type: 'gcs',
        bucketName: FileCopyHelper.bucketDestination,
        subFolderPrefix: destination.subFolderPrefix.replace(/\/?$/, '/'),
        keepTree,
        overwriteExisting,
      },
    }
  }, [selection, destination, keepTree, overwriteExisting])

  const isSelectionEmpty = selection.folders.size === 0 && selection.files.size === 0

  const dryRunMutation = useMutation<FileCopyDryRunResult, Error, CreateFileCopyPayload>({
    mutationFn: payload => FileCopyService.dryRun(payload),
  })

  const createMutation = useMutation<{ id: string }, Error, CreateFileCopyPayload>({
    mutationFn: payload => FileCopyService.createOperation(payload),
    onSuccess: operation => {
      clearSelection()
      onCreated?.(operation.id)
    },
  })

  const statsById = useQuery({
    queryKey: ['fileCopy', 'stats', Array.from(selection.folders.keys()).sort()],
    queryFn: async () => {
      const ids = Array.from(selection.folders.keys()).sort()
      const results = await Promise.all(ids.map(id => FileCopyService.getFolderStats(id)))
      return results.reduce(
        (acc, stat) => ({
          fileCount: acc.fileCount + stat.fileCount,
          totalBytes: acc.totalBytes + stat.totalBytes,
          unsupportedCount: acc.unsupportedCount + stat.unsupportedCount,
        }),
        { fileCount: 0, totalBytes: 0, unsupportedCount: 0 },
      )
    },
    enabled: selection.folders.size > 0,
  })

  const totalFiles = (statsById.data?.fileCount ?? 0) + selection.files.size
  const totalBytes =
    (statsById.data?.totalBytes ?? 0) +
    Array.from(selection.files.values()).reduce((sum, file) => sum + (file.sizeBytes ?? 0), 0)

  const handleDryRun = () => dryRunMutation.mutate(buildPayload())
  const handleCreate = useCallback(async () => {
    try {
      const res = await createMutation.mutateAsync(buildPayload())

      navigate(`/file-copy/${res.id}`)
    } catch (error) {
      console.log(error)
    }
  }, [createMutation.mutate, navigate, buildPayload])

  const canSubmit = !isSelectionEmpty && !createMutation.isLoading

  return (
    <Stack gap="md">
      <Title order={4}>{t('pages.fileCopy.create.title')}</Title>

      <Group align="flex-start" gap="lg" grow preventGrowOverflow={false}>
        <Card withBorder radius="md" padding="sm" style={{ flex: 1, minWidth: 320 }} h="100%">
          <Stack gap="xs">
            <Group justify="space-between">
              <Title order={5}>{t('pages.fileCopy.create.source')}</Title>
              {path.length > 0 && (
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={() => {
                    const parent = path[path.length - 2]
                    setPath(p => p.slice(0, -1))
                    setBrowseFolderId(parent ? parent.id : null)
                  }}
                >
                  {t('pages.fileCopy.create.back')}
                </Button>
              )}
            </Group>

            {path.length > 0 && (
              <Group gap={4}>
                <Text size="xs" c="dimmed">
                  {path.map((p, i) => (
                    <span key={p.id}>
                      {i > 0 && ' / '}
                      <button
                        type="button"
                        style={{
                          cursor: 'pointer',
                          color: 'var(--mantine-color-blue-6)',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          font: 'inherit',
                        }}
                        onClick={() => {
                          setPath(prev => prev.slice(0, i + 1))
                          setBrowseFolderId(p.id)
                        }}
                      >
                        {p.name}
                      </button>
                    </span>
                  ))}
                </Text>
              </Group>
            )}

            {rootsQuery.isLoading || (browseFolderId && childrenQuery.isLoading) ? (
              <Box py="xl" style={{ display: 'grid', placeItems: 'center' }}>
                <Loader size="sm" />
              </Box>
            ) : (
              <ScrollArea.Autosize mah={536} type="auto">
                <Stack gap={2}>
                  {entries.map(entry => (
                    <Group
                      key={entry.id}
                      justify="space-between"
                      wrap="nowrap"
                      style={{
                        opacity: !entry.isFolder && entry.isSupported === false ? 0.4 : 1,
                        borderBottom: '1px solid var(--mantine-color-gray-1)',
                        paddingBottom: 4,
                      }}
                    >
                      <Group gap="sm" wrap="nowrap">
                        <Checkbox
                          size="xs"
                          checked={
                            entry.isFolder
                              ? selection.folders.has(entry.id)
                              : selection.files.has(entry.id)
                          }
                          onChange={() => toggleEntry(entry)}
                          disabled={!entry.isFolder && entry.isSupported === false}
                        />
                        <button
                          type="button"
                          style={{
                            cursor: entry.isFolder ? 'pointer' : 'default',
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            font: 'inherit',
                            fontSize: 'var(--mantine-font-size-sm)',
                            textAlign: 'left',
                          }}
                          onClick={() => {
                            if (entry.isFolder) {
                              setPath(p => [...p, { id: entry.id, name: entry.name }])
                              setBrowseFolderId(entry.id)
                            }
                          }}
                        >
                          <Group gap="xs" align="center">
                            {entry.isFolder ? (
                              <IconFolderFilled color="var(--mantine-color-blue-4)" size={20} />
                            ) : (
                              <Badge
                                size="sm"
                                variant="light"
                                color={entry.isFolder ? 'blue' : 'gray'}
                              >
                                {entry.mimeType?.split('/').pop()?.toUpperCase() ??
                                  t('_generics.file')}
                              </Badge>
                            )}
                            {entry.name}
                          </Group>
                        </button>
                      </Group>
                      {!entry.isFolder && entry.sizeBytes != null && (
                        <Text size="xs" c="dimmed">
                          {(entry.sizeBytes / 1024 / 1024).toFixed(1)} Mo
                        </Text>
                      )}
                    </Group>
                  ))}
                </Stack>
              </ScrollArea.Autosize>
            )}
          </Stack>
        </Card>

        <Stack gap="md" style={{ flex: 1, minWidth: 320 }}>
          <Card withBorder radius="md" padding="sm">
            <Stack gap="sm">
              <Title order={5}>{t('pages.fileCopy.create.destination')}</Title>
              <Stack gap={2} mb="md">
                <Text size="sm">{t('pages.fileCopy.create.bucket')}</Text>
                <Text fw={600}>{FileCopyHelper.bucketDestination}</Text>
                <Text size="xs" c="dimmed" fs="italic">
                  {t('pages.fileCopy.create.fixedBucketDestination')}
                </Text>
              </Stack>
              <TextInput
                label={t('pages.fileCopy.create.prefix')}
                placeholder={t('pages.fileCopy.create.prefixPlaceholder')}
                value={destination.subFolderPrefix}
                onChange={e => setDestination(d => ({ ...d, subFolderPrefix: e.target.value }))}
              />
              <Switch
                label={t('pages.fileCopy.create.keepTree')}
                checked={keepTree}
                onChange={e => setKeepTree(e.target.checked)}
              />
              <Switch
                label={t('pages.fileCopy.create.overwrite')}
                checked={overwriteExisting}
                onChange={e => setOverwriteExisting(e.target.checked)}
              />
            </Stack>
          </Card>

          <Paper withBorder radius="md" p="sm">
            <Stack gap="xs">
              <Title order={5}>{t('pages.fileCopy.create.recap')}</Title>
              <Group justify="space-between" gap="xs" wrap="nowrap">
                <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
                  {t('pages.fileCopy.create.recapSource')}
                </Text>
                <Text size="sm" fw={500} ta="right" lineClamp={1}>
                  Drive{path.length > 0 && ` / ${path.map(p => p.name).join(' / ')}`}
                </Text>
              </Group>
              <Group justify="space-between" gap="xs" wrap="nowrap">
                <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
                  {t('pages.fileCopy.create.recapSelection')}
                </Text>
                <Text size="sm" fw={500} ta="right">
                  {t('pages.fileCopy.create.selectionSummary', {
                    folders: selection.folders.size,
                    files: selection.files.size,
                  })}
                </Text>
              </Group>
              {!isSelectionEmpty && (
                <Stack gap={4}>
                  <ScrollArea.Autosize mah={220} type="auto">
                    <Stack gap={2}>
                      {(['folders', 'files'] as const).flatMap(kind =>
                        Array.from(selection[kind].values()).map(item => (
                          <Group key={item.id} justify="space-between" gap="xs" wrap="nowrap">
                            <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                              {kind === 'folders' && (
                                <IconFolderFilled
                                  color="var(--mantine-color-blue-4)"
                                  size={16}
                                  style={{ flexShrink: 0 }}
                                />
                              )}
                              <Stack gap={0} style={{ minWidth: 0 }}>
                                <Text size="sm" lineClamp={1}>
                                  {item.name}
                                </Text>
                                <Text size="xs" c="dimmed" lineClamp={1}>
                                  {item.location}
                                  {kind === 'files' &&
                                    item.sizeBytes != null &&
                                    ` · ${NumberFormatter.formatDigitalByte(item.sizeBytes)}`}
                                </Text>
                              </Stack>
                            </Group>
                            <ActionIcon
                              size="sm"
                              variant="subtle"
                              color="gray"
                              aria-label={t('pages.fileCopy.create.unselect', { name: item.name })}
                              onClick={() => unselect(kind, item.id)}
                            >
                              <IconX size={14} />
                            </ActionIcon>
                          </Group>
                        )),
                      )}
                    </Stack>
                  </ScrollArea.Autosize>
                  <Group justify="flex-end">
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      color="gray"
                      onClick={clearSelection}
                    >
                      {t('pages.fileCopy.create.clearSelection')}
                    </Button>
                  </Group>
                </Stack>
              )}
              <Group justify="space-between" gap="xs" wrap="nowrap">
                <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
                  {t('pages.fileCopy.create.recapDestination')}
                </Text>
                <Text size="sm" fw={500} ta="right" lineClamp={1}>
                  gs://{FileCopyHelper.bucketDestination}
                  {destination.subFolderPrefix
                    ? `/${destination.subFolderPrefix.replace(/^\//, '')}`
                    : ''}
                </Text>
              </Group>
              <Group justify="space-between" gap="xs" wrap="nowrap">
                <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
                  {t('pages.fileCopy.create.recapTree')}
                </Text>
                <Text size="sm" fw={500} ta="right">
                  {t(
                    keepTree
                      ? 'pages.fileCopy.create.recapTreeKept'
                      : 'pages.fileCopy.create.recapTreeNotKept',
                  )}
                </Text>
              </Group>
              <Group justify="space-between" gap="xs" wrap="nowrap">
                <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
                  {t('pages.fileCopy.create.recapExisting')}
                </Text>
                <Text size="sm" fw={500} ta="right">
                  {t(
                    overwriteExisting
                      ? 'pages.fileCopy.create.recapOverwriteYes'
                      : 'pages.fileCopy.create.recapOverwriteNo',
                  )}
                </Text>
              </Group>
              {statsById.isFetching ? (
                <Loader size="xs" />
              ) : (
                <Center mt="sm">
                  <Title order={6}>
                    {t('pages.fileCopy.create.estimatedTotal', {
                      files: totalFiles.toLocaleString('fr-FR'),
                      size: NumberFormatter.formatDigitalByte(totalBytes),
                    })}
                  </Title>
                  {(statsById.data?.unsupportedCount ?? 0) > 0 && (
                    <Text size="xs" c="dimmed">
                      {t('pages.fileCopy.create.unsupportedIgnored', {
                        count: statsById.data?.unsupportedCount,
                      })}
                    </Text>
                  )}
                </Center>
              )}
              <Group justify="flex-end" mt="xs">
                <Button variant="outline" onClick={handleDryRun} disabled={!canSubmit}>
                  {t('pages.fileCopy.create.dryRun')}
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={!canSubmit}
                  loading={createMutation.isLoading}
                >
                  {t('pages.fileCopy.create.launch')}
                </Button>
              </Group>
            </Stack>
          </Paper>

          {dryRunMutation.data && (
            <Alert color="blue" title={t('pages.fileCopy.create.dryRunResult')}>
              {t('pages.fileCopy.create.dryRunSummary', {
                files: dryRunMutation.data.filesCount.toLocaleString('fr-FR'),
                size: NumberFormatter.formatDigitalByte(dryRunMutation.data.totalBytes),
              })}
              {dryRunMutation.data.existingCount > 0 &&
                t('pages.fileCopy.create.existingAtDestination', {
                  count: dryRunMutation.data.existingCount,
                })}
            </Alert>
          )}

          {createMutation.isError && (
            <Alert color="red" title={t('pages.fileCopy.create.error')}>
              {createMutation.error.message}
            </Alert>
          )}
        </Stack>
      </Group>
    </Stack>
  )
}
